import { reactive } from 'vue';

const BATCH_SIZE = 100
const TTL = 5 * 60 * 1000

const translationCache = {}
const pendingRequests = {}
const batchQueue = []
let batchTimer = null
let weglotReadyPromise = null

// hash function to safely convert any text into a storage-safe key
function hashString(str) {
    let hash = 2166136261

    for (let i = 0; i < str.length; i++) {
        hash ^= str.charCodeAt(i);
        hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
    }

    return (hash >>> 0).toString(16);
}

function ensureReactiveStore(app) {
    if (!app.config.globalProperties._translations) {
        app.config.globalProperties._translations = reactive({});
    }
}

function getStored(storageKey) {
    try {
        const raw = localStorage.getItem(storageKey); // eslint-disable-line compat/compat

        if (!raw) {
            return null;
        }

        const { v, e } = JSON.parse(raw);

        if (Date.now() > e) {
            localStorage.removeItem(storageKey); // eslint-disable-line compat/compat
            return null;
        }

        return v;
    } catch {
        return null;
    }
}

function setStored(storageKey, value) {
    try {
        localStorage.setItem(storageKey, JSON.stringify({ v: value, e: Date.now() + TTL })); // eslint-disable-line compat/compat
    } catch {
        // storage quota exceeded — skip caching
    }
}

function waitForWeglot() {
    if (!weglotReadyPromise) {
        weglotReadyPromise = new Promise(resolve => { // eslint-disable-line compat/compat
            const interval = setInterval(() => {
                if (('Weglot' in window) === false) {
                    return;
                }

                if (!Weglot.initialized) { // eslint-disable-line no-undef
                    return;
                }

                clearInterval(interval);

                resolve();
            });
        });
    }

    return weglotReadyPromise;
}

function translateChunk(chunk, store) {
    return new Promise(resolve => { // eslint-disable-line compat/compat
        Weglot.translate({ // eslint-disable-line no-undef
            words: chunk.map(({ key }) => ({ w: key, t: 1 })),
            languageTo: Weglot.getCurrentLang(), // eslint-disable-line no-undef
        }, data => {
            chunk.forEach(({ key, storageKey }, i) => {
                const translated = (data && data[i]) || key;
                translationCache[key] = translated;
                store[key] = translated;

                if (translated !== key) {
                    setStored(storageKey, translated);
                }

                delete pendingRequests[key];
            });

            resolve();
        });
    });
}

async function flushBatch(app) {
    batchTimer = null;

    const batch = batchQueue.splice(0);

    if (!batch.length) {
        return;
    }

    const store = app.config.globalProperties._translations;

    try {
        await waitForWeglot();

        if (Weglot.getCurrentLang() === 'en') { // eslint-disable-line no-undef
            batch.forEach(({ key }) => delete pendingRequests[key]);
            return;
        }

        const chunks = []

        for (let i = 0; i < batch.length; i += BATCH_SIZE) {
            chunks.push(batch.slice(i, i + BATCH_SIZE));
        }

        for (const chunk of chunks) {
            await translateChunk(chunk, store);
        }
    } catch {
        batch.forEach(({ key }) => {
            store[key] = key;
            delete pendingRequests[key];
        });
    }
}

function fetchTranslation(app, key) {
    if (!key) {
        return key;
    }

    // early exit: only translate on allowed domains
    const hostname = window.location.hostname;

    if (!hostname.endsWith('.com') && !hostname.endsWith('.devspace.net')) {
        return key;
    }

    const storageKey = `_t${hashString(key)}`;

    const storedValue = getStored(storageKey);

    if (storedValue) {
        return storedValue;
    }

    ensureReactiveStore(app);
    const store = app.config.globalProperties._translations;

    if (translationCache[key]) {
        store[key] = translationCache[key];
        return store[key];
    }

    if (pendingRequests[key]) {
        return store[key] || key;
    }

    // show original while translation loads
    store[key] = key;
    pendingRequests[key] = true;

    batchQueue.push({ key, storageKey });

    clearTimeout(batchTimer);
    batchTimer = setTimeout(() => flushBatch(app), 50);

    return store[key];
}

export default {
    install(app) {
        // Global function for templates and script setup
        app.config.globalProperties.$t = function (key) {
            return fetchTranslation(app, key);
        }
    }
}
